/**
 * Service Type Color System
 * Maps service types to consistent color schemes for calendar events
 */

export const SERVICE_COLORS = {
  worship: {
    bg: "bg-blue-50 dark:bg-blue-900/20",
    bgHover: "hover:bg-blue-100 dark:hover:bg-blue-900/30",
    border: "border-blue-200 dark:border-blue-700",
    accent: "bg-blue-500",
    text: "text-blue-700 dark:text-blue-300",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
  },
  prayer: {
    bg: "bg-purple-50 dark:bg-purple-900/20",
    bgHover: "hover:bg-purple-100 dark:hover:bg-purple-900/30",
    border: "border-purple-200 dark:border-purple-700",
    accent: "bg-purple-500",
    text: "text-purple-700 dark:text-purple-300",
    badge: "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300",
  },
  outreach: {
    bg: "bg-green-50 dark:bg-green-900/20",
    bgHover: "hover:bg-green-100 dark:hover:bg-green-900/30",
    border: "border-green-200 dark:border-green-700",
    accent: "bg-green-500",
    text: "text-green-700 dark:text-green-300",
    badge: "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300",
  },
  youth: {
    bg: "bg-orange-50 dark:bg-orange-900/20",
    bgHover: "hover:bg-orange-100 dark:hover:bg-orange-900/30",
    border: "border-orange-200 dark:border-orange-700",
    accent: "bg-orange-500",
    text: "text-orange-700 dark:text-orange-300",
    badge: "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300",
  },
  children: {
    bg: "bg-yellow-50 dark:bg-yellow-900/20",
    bgHover: "hover:bg-yellow-100 dark:hover:bg-yellow-900/30",
    border: "border-yellow-200 dark:border-yellow-700",
    accent: "bg-yellow-500",
    text: "text-yellow-700 dark:text-yellow-300",
    badge: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300",
  },
  special: {
    bg: "bg-pink-50 dark:bg-pink-900/20",
    bgHover: "hover:bg-pink-100 dark:hover:bg-pink-900/30",
    border: "border-pink-200 dark:border-pink-700",
    accent: "bg-pink-500",
    text: "text-pink-700 dark:text-pink-300",
    badge: "bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300",
  },
  meeting: {
    bg: "bg-surface-raised",
    bgHover: "hover:bg-surface-subtle",
    border: "border-line",
    accent: "bg-fg-muted",
    text: "text-fg-muted",
    badge: "bg-surface-subtle text-fg-muted",
  },
  default: {
    bg: "bg-brand-50 dark:bg-brand-900/20",
    bgHover: "hover:bg-brand-100 dark:hover:bg-brand-900/30",
    border: "border-brand-200 dark:border-brand-700",
    accent: "bg-brand-500",
    text: "text-brand-700 dark:text-brand-300",
    badge: "bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300",
  },
};

/**
 * Get color classes for a service type
 * @param {string} type - Service type (worship, prayer, etc.)
 * @returns {object} Color classes for the service type
 */
export const getServiceColors = (type) => {
  if (!type) return SERVICE_COLORS.default;

  // Normalize the type string
  const normalizedType = type.toLowerCase().trim();

  // Check for exact match
  if (SERVICE_COLORS[normalizedType]) {
    return SERVICE_COLORS[normalizedType];
  }

  // Check for partial matches
  if (normalizedType.includes("worship")) return SERVICE_COLORS.worship;
  if (normalizedType.includes("prayer")) return SERVICE_COLORS.prayer;
  if (normalizedType.includes("outreach")) return SERVICE_COLORS.outreach;
  if (normalizedType.includes("youth")) return SERVICE_COLORS.youth;
  if (normalizedType.includes("children") || normalizedType.includes("kids")) return SERVICE_COLORS.children;
  if (normalizedType.includes("special") || normalizedType.includes("event")) return SERVICE_COLORS.special;
  if (normalizedType.includes("meeting")) return SERVICE_COLORS.meeting;

  // Default fallback
  return SERVICE_COLORS.default;
};

/**
 * Get confirmation status summary for assigned people
 * @param {Array} users - Array of assigned users with status
 * @returns {object} Status counts and percentages
 */
export const getConfirmationStatus = (users = []) => {
  if (!users || users.length === 0) {
    return {
      total: 0,
      confirmed: 0,
      pending: 0,
      declined: 0,
      percentage: 0,
    };
  }

  const confirmed = users.filter((u) => u?.state === "confirmed" || u?.status === "confirmed").length;
  const declined = users.filter((u) => u?.state === "declined" || u?.status === "declined").length;
  const pending = users.length - confirmed - declined;

  return {
    total: users.length,
    confirmed,
    pending,
    declined,
    percentage: Math.round((confirmed / users.length) * 100),
  };
};
